import { fetchAuthSession } from 'aws-amplify/auth'

import { SECURE_API_BASE, API_BASE } from '../config'

let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null
let onTokenRefreshed: ((token: string) => void) | null = null

export function registerTokenRefreshCallback(cb: (token: string) => void) {
  onTokenRefreshed = cb
}

async function refreshToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true })
      const token = session.tokens?.idToken?.toString() || null
      if (token) onTokenRefreshed?.(token)
      return token
    } catch (error: unknown) {
      console.error('[secureFetch] Token refresh failed:', error)
      return null
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

async function getFreshToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString() || null
    if (!token) return null
    // Check if token expires within 60 seconds
    const tokenParts = token.split('.')
    const payloadPart = tokenParts[1]
    if (!payloadPart) return null
    const payload = JSON.parse(atob(payloadPart))
    if (payload.exp && payload.exp - Date.now() / 1000 < 60) {
      return await refreshToken()
    }
    onTokenRefreshed?.(token)
    return token
  } catch {
    return null
  }
}

// package-local: Error subclass for API errors with status and arbitrary response fields
class ApiError extends Error {
  status: number = 0;
  [key: string]: unknown
}

export async function secureFetch<T = unknown>(url: string, idToken: string, opts: RequestInit = {}): Promise<T> {
  const freshToken = await getFreshToken()
  const tokenToUse = freshToken || idToken

  const makeRequest = async (token: string) => {
    return fetch(url, {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
  }

  let resp = await makeRequest(tokenToUse)

  // If 401, try to force-refresh token and retry once
  if (resp.status === 401) {
    console.log('[secureFetch] Got 401, attempting token refresh...')
    const newToken = await refreshToken()

    if (newToken) {
      console.log('[secureFetch] Token refreshed, retrying request...')
      resp = await makeRequest(newToken)
    } else {
      console.error('[secureFetch] Token refresh failed, redirecting to login')
      if (typeof window !== 'undefined') {
        alert('Your session has expired. Please log in again.')
        window.location.href = '/'
      }
      throw new Error('Session expired - please log in again')
    }
  }

  const tryParse = async () => {
    const text = await resp.text()
    if (!text) return null
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }

  // Justified coercion: null/parsed values are valid T at runtime for these status codes
  if (resp.status === 204) return null as T
  if (resp.status === 202) return await tryParse() as T

  if (!resp.ok) {
    const errData = (await tryParse()) || 'API error'

    const error = new ApiError()
    if (typeof errData === 'object' && errData !== null) {
      Object.assign(error, errData)
      // Justified coercion: errData is confirmed to be a non-null object by the typeof check above
      const data = errData as Record<string, unknown>
      error.message = (typeof data.message === 'string' ? data.message : undefined)
        || (typeof data.details === 'string' ? data.details : undefined)
        || 'API error'
      error.status = resp.status
    } else {
      error.message = typeof errData === 'string' ? errData : JSON.stringify(errData)
      error.status = resp.status
    }

    throw error
  }

  return await tryParse()
}

export { SECURE_API_BASE, API_BASE }
